import { useState } from 'react';
import { Paper, Box, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { Pencil } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { organizationEndpoints } from '../../services/api/endpoints/organisation.endpoint';
import OrganizationInfo from '../../components/settings/OrganizationInfo';
import OrganizationForm from '../../components/settings/OrganizationForm';
import Notifications from '../../components/common/Notifications';
import { fetchOrganiationsById } from '../../store/slices/organizationSlice';
import { NotificationT, SEVERITY } from '../../types/notification';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';

const PersonalizationSettings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });
  const { role } = useAuth();

  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );

  const handleUpdate = async (data: any) => {
    if (!selectedOrganization) return;

    setIsLoading(true);
    setError(null);

    try {
      const updatedData = {
        ...data,
        isBookingEnabled: data.isBookingEnabled,
        bookingDescription: data.bookingDescription,
        ingest_status: data.ingest_status,
      };
      await organizationEndpoints.updateOrganisation(selectedOrganization.id, updatedData);
      await dispatch(fetchOrganiationsById(selectedOrganization.id));
      setNotification({
        open: true,
        message: 'Organization updated successfully',
        severity: SEVERITY.SUCCESS,
      });
      setIsEditing(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update organization';
      setError(message);
      setNotification({
        open: true,
        message,
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!selectedOrganization) {
    return <Alert severity="info">Please select an organization to view settings.</Alert>;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        backgroundColor: 'theme.palette.background.paper',
        borderRadius: 1,
        border: '1px solid theme.palette.divider',
        '&:hover': {
          borderColor: 'theme.palette.primary.main',
          transition: 'border-color 0.3s ease',
        },
      }}
      className="w-full xl:max-w-4xl mx-auto mt-10"
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          {selectedOrganization.company.name} profile information
        </Typography>
        {role !== AUTH_CONSTANTS.ROLES.USER && !isEditing ? (
          <Button
            variant="contained"
            disableElevation
            startIcon={<Pencil size={16} />}
            onClick={() => setIsEditing(true)}
          >
            Edit Organization
          </Button>
        ) : null}
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Paper elevation={0} sx={{ pt: 2 }}>
        {isEditing ? (
          <OrganizationForm
            organization={selectedOrganization}
            onSubmit={handleUpdate}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <OrganizationInfo organization={selectedOrganization} />
        )}
      </Paper>
      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Paper>
  );
};

export default PersonalizationSettings;
